import { useState } from 'react';
import { Container, Title, Table, Button, Group, Modal, Stack, Tabs, Badge, Text, Textarea, NumberInput, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconArrowBack } from '@tabler/icons-react';
import { useClaims, useFileQualityClaim, useFileReturn, useClaimDetail } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  approved: 'green',
  denied: 'red',
  resolved: 'teal',
};

export default function ClaimsCenter() {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const { data } = useClaims(statusFilter ?? undefined);
  const fileClaimMut = useFileQualityClaim();
  const fileReturnMut = useFileReturn();
  const [claimOpened, { open: openClaim, close: closeClaim }] = useDisclosure(false);
  const [returnOpened, { open: openReturn, close: closeReturn }] = useDisclosure(false);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);
  const [selectedClaim, setSelectedClaim] = useState<{ id: number; type: string } | null>(null);

  // Claim form
  const [claimType, setClaimType] = useState('quality');
  const [claimAmount, setClaimAmount] = useState<number | ''>(0);
  const [claimDesc, setClaimDesc] = useState('');

  // Return form
  const [returnOrderId, setReturnOrderId] = useState<number | ''>('');
  const [returnReason, setReturnReason] = useState('');
  const [returnNotes, setReturnNotes] = useState('');

  const claims = data?.claims ?? [];
  const { data: detailData } = useClaimDetail(selectedClaim?.type ?? '', selectedClaim?.id ?? 0);

  const handleFileClaim = () => {
    fileClaimMut.mutate({ claim_type: claimType, amount: Number(claimAmount), description: claimDesc }, {
      onSuccess: () => { closeClaim(); notifications.show({ title: 'Filed', message: 'Quality claim submitted', color: 'green' }); },
    });
  };

  const handleFileReturn = () => {
    if (!returnOrderId) return;
    fileReturnMut.mutate({ order_id: Number(returnOrderId), items: [], reason_code: returnReason, notes: returnNotes }, {
      onSuccess: () => { closeReturn(); notifications.show({ title: 'Filed', message: 'Return request submitted', color: 'green' }); },
    });
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Claims & Returns</Title>
        <Group>
          <Button leftSection={<IconAlertTriangle size={16} />} onClick={openClaim}>File Quality Claim</Button>
          <Button leftSection={<IconArrowBack size={16} />} variant="outline" onClick={openReturn}>File Return</Button>
        </Group>
      </Group>

      <Tabs defaultValue="all" mb="md" onChange={(v) => setStatusFilter(v === 'all' ? null : v)}>
        <Tabs.List>
          <Tabs.Tab value="all">All</Tabs.Tab>
          <Tabs.Tab value="pending">Pending</Tabs.Tab>
          <Tabs.Tab value="approved">Approved</Tabs.Tab>
          <Tabs.Tab value="resolved">Resolved</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Reference</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {claims.map((c) => (
            <Table.Tr key={`${c.type}-${c.id}`}>
              <Table.Td>{c.reference}</Table.Td>
              <Table.Td><Badge variant="light">{c.type}</Badge></Table.Td>
              <Table.Td>${c.amount.toFixed(2)}</Table.Td>
              <Table.Td><Badge color={STATUS_COLORS[c.status] ?? 'gray'}>{c.status}</Badge></Table.Td>
              <Table.Td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</Table.Td>
              <Table.Td>
                <Button size="xs" variant="subtle" onClick={() => { setSelectedClaim({ id: c.id, type: c.type }); openDetail(); }}>
                  View
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={claimOpened} onClose={closeClaim} title="File Quality Claim" size="md">
        <Stack>
          <Select label="Claim Type" data={['quality', 'temperature', 'condition', 'grade', 'size', 'other']}
            value={claimType} onChange={(v) => setClaimType(v ?? 'quality')} />
          <NumberInput label="Amount ($)" value={claimAmount} onChange={(v) => setClaimAmount(typeof v === 'number' ? v : '')} decimalScale={2} />
          <Textarea label="Description" value={claimDesc} onChange={(e) => setClaimDesc(e.currentTarget.value)} />
          <Button onClick={handleFileClaim} loading={fileClaimMut.isPending}>Submit Claim</Button>
        </Stack>
      </Modal>

      <Modal opened={returnOpened} onClose={closeReturn} title="File Return Request" size="md">
        <Stack>
          <NumberInput label="Order ID" value={returnOrderId} onChange={(v) => setReturnOrderId(typeof v === 'number' ? v : '')} />
          <Select label="Reason" data={['damaged', 'wrong_item', 'quality', 'overshipment', 'customer_request']}
            value={returnReason} onChange={(v) => setReturnReason(v ?? '')} />
          <Textarea label="Notes" value={returnNotes} onChange={(e) => setReturnNotes(e.currentTarget.value)} />
          <Button onClick={handleFileReturn} loading={fileReturnMut.isPending}>Submit Return</Button>
        </Stack>
      </Modal>

      <Modal opened={detailOpened} onClose={closeDetail} title="Claim Detail" size="lg">
        {detailData ? (
          <Stack>
            <Group><Text fw={600}>Reference:</Text><Text>{detailData.reference}</Text></Group>
            <Group><Text fw={600}>Type:</Text><Badge>{detailData.type}</Badge></Group>
            <Group><Text fw={600}>Status:</Text><Badge color={STATUS_COLORS[detailData.status] ?? 'gray'}>{detailData.status}</Badge></Group>
            {detailData.amount !== undefined && <Group><Text fw={600}>Amount:</Text><Text>${detailData.amount.toFixed(2)}</Text></Group>}
            {detailData.notes && <Group><Text fw={600}>Notes:</Text><Text>{detailData.notes}</Text></Group>}
          </Stack>
        ) : <Text c="dimmed">Loading...</Text>}
      </Modal>
    </Container>
  );
}
